import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useActivities } from '../hooks/useActivities.js';
import RandomRunMap from '../components/RandomRunMap.jsx';
import RecentRunsCarousel from '../components/RecentRunsCarousel.jsx';
import AllRunsMap from '../components/AllRunsMap.jsx';
import MapFilter from '../components/MapFilter.jsx';
import Footer from '../components/Footer.jsx';
import { blogPosts, getPreviousRace, getNextRace, excerptFor } from '../lib/content.js';

const DEFAULT_RACE_HEADER = '/images/default_trail_header-heading.jpg';
const DEFAULT_RACE_BANNER = '/images/default_trail_header-race-banner.jpg';

function RaceCard({ label, race }) {
  return (
    <div className="col-8 col-lg-5">
      <div>
        <div className="row" style={{ margin: 0 }}>
          <div className="col header-text">{label}</div>
        </div>
        <div className="row p-row">
          <div className="col">
            <div className="row">
              <div className="col image-center">
                <Link to={`/races/${race.slug}`}>
                  <img
                    className="img-fluid hp-race-image"
                    src={race.headerImage || DEFAULT_RACE_HEADER}
                    alt={race.title}
                  />
                </Link>
              </div>
            </div>
            <div className="row">
              <div className="col-12 race-excerpt-title">
                <Link to={`/races/${race.slug}`}>{race.title}</Link>
              </div>
            </div>
            <div className="row">
              <div className="col-12 p-text text-center" style={{ color: 'white' }}>
                {race.tagline}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function NoUpcomingRaceCard() {
  return (
    <div className="col-8 col-lg-5">
      <div>
        <div className="row" style={{ margin: 0 }}>
          <div className="col header-text">Upcoming Race</div>
        </div>
        <div className="row p-row">
          <div className="col">
            <div className="row">
              <div className="col image-center">
                <img className="img-fluid hp-race-image" src={DEFAULT_RACE_BANNER} alt="" />
              </div>
            </div>
            <div className="row">
              <div className="col-12 race-excerpt-title">No Upcoming Races</div>
            </div>
            <div className="row">
              <div className="col-12 p-text text-center" style={{ color: 'white' }}>
                Check back soon for my future plans!
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function FrontPage() {
  const { data } = useActivities();
  const navigate = useNavigate();
  const [timeFrame, setTimeFrame] = useState('');

  const prevRace = getPreviousRace();
  const nextRace = getNextRace();

  // The original front page locks the window and scroll-snaps an inner container.
  useEffect(() => {
    const root = document.documentElement;
    const prevHtmlOverflow = root.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;
    root.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    return () => {
      root.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
    };
  }, []);

  const useableData = data ? data.length : 0;

  return (
    <div id="snapContainer">
      {/* Hero */}
      <div id="hpHome" className="hp-head-container container-fluid">
        <div id="toggleData" className="wiggle" onClick={() => navigate('/your-map')}>
          See YOUR Data!
        </div>
        <div className="row hp-head-row">
          <div className="col-lg-1 col-0"></div>
          <div className="col-lg-4 col-12 hp-head-col name">
            <div className="row name">
              <h1>LOGAN</h1>
            </div>
            <div className="row name">
              <h1>HOLMES</h1>
            </div>
            <div className="row name">
              <h1>RUNS</h1>
            </div>
          </div>
          <div className="col-lg-6 col-12 hp-head-col tag">
            <p>Keep up with my running</p>
          </div>
          <div className="col-lg-1 col-0"></div>
        </div>
        <div className="arrow-bounce bounce"></div>
      </div>

      {/* Random run showcase */}
      {useableData > 1 ? (
        <RandomRunMap data={data} />
      ) : (
        <div id="randomMapContainer">
          <div id="randomMap"></div>
        </div>
      )}

      {/* Races */}
      <div id="hpRaces" className="container-fluid hp-races-container">
        <div className="row races-flex">
          <div className="col-1"></div>
          {prevRace && <RaceCard label="Previous Race" race={prevRace} />}
          {nextRace ? <RaceCard label="Upcoming Race" race={nextRace} /> : <NoUpcomingRaceCard />}
          <div className="col-1"></div>
        </div>
      </div>

      {/* Recent runs carousel */}
      <div id="hpRecentRuns" className="container-fluid recent-runs">
        <div className="row">
          <div className="col-12" style={{ padding: 0 }}>
            {useableData > 11 && <RecentRunsCarousel data={data} />}
          </div>
        </div>
      </div>

      {/* Blog */}
      <div id="hpBlog" className="container-fluid blog">
        <div className="row">
          <div className="col-1"></div>
          <div className="col-10">
            <div className="row">
              <div
                className="col header-text"
                style={{ textAlign: 'left', color: 'var(--black)' }}
              >
                Blog
              </div>
            </div>
            <div className="remSB">
              <div className="row scroll-box hp-sb">
                <div className="col-12">
                  {blogPosts.map((post) => (
                    <div className="row" key={post.slug}>
                      <div className="col blog-post-div border-black">
                        <div className="row">
                          <div className="col-3 image-center">
                            <Link to={`/blog/${post.slug}`}>
                              <img
                                className="img-fluid fp-blog-image border-black"
                                src={post.thumbnail || DEFAULT_RACE_HEADER}
                                alt={post.title}
                              />
                            </Link>
                          </div>
                          <div className="col-9">
                            <div className="row">
                              <div className="col blog-excerpt-title">
                                <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                              </div>
                            </div>
                            <div className="row">
                              <div className="col blog-excerpt-text">
                                {excerptFor(post)}{' '}
                                <u>
                                  <Link to={`/blog/${post.slug}`}>Read More</Link>
                                </u>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="col-1"></div>
        </div>
      </div>

      {/* All runs map */}
      <div id="allRunsMapContainer">
        <MapFilter onFilter={setTimeFrame} />
        {useableData > 0 && (
          <AllRunsMap data={data} timeFrame={timeFrame} style={{ height: 'inherit' }} />
        )}
      </div>

      <div className="container-fliud">
        <Footer />
      </div>
    </div>
  );
}
